import { Text } from "@/atoms"
import { DeployCommand } from "@/pages/DeploymentsPage/components/molecules/DeployCommand"
import { InlineField } from "@/pages/DeploymentsPage/components/molecules/InlineField"
import { getScriptDeployCommandString } from "@/pages/DeploymentsPage/helpers/deployment-command"

import styles from "./ScriptDeployCommand.module.scss"

import type {
  LineButton,
  ScriptDeployCommandProps
} from "./ScriptDeployCommand.types"

const getStaticDefaultValues = () => ({ key: Date.now().toString() })

// A deploy command made of several shell commands, rendered line by line with
// an inline input for every named value; lineButtons adds a button (e.g. the
// generate-key button) after the input of the given lines.
export const ScriptDeployCommand = <Key extends string>(
  props: ScriptDeployCommandProps<Key>
) => {
  const {
    lines,
    groups,
    lineButtons,
    getDefaultValues = getStaticDefaultValues,
    ...rest
  } = props

  return (
    <DeployCommand
      getDefaultValues={getDefaultValues}
      getDeployCommand={(values, defaultValues) =>
        getScriptDeployCommandString({ lines, groups, values, defaultValues })
      }
      {...rest}
    >
      {({ onError, ...fieldProps }) =>
        groups.flatMap((keys) =>
          keys.map((key, index) => {
            const line = lines[key]
            const Button: LineButton | undefined = lineButtons?.[key]

            if (line.comment)
              return (
                <Text key={key} className={styles.comment}>
                  {line.text}
                </Text>
              )

            return (
              <InlineField
                key={key}
                line={line}
                noNullCharacter={index === keys.length - 1}
                buttons={Button && <Button onError={onError} />}
                {...fieldProps}
              />
            )
          })
        )
      }
    </DeployCommand>
  )
}
